import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import NotFoundPage from './NotFoundPage';
import './DynamicPage.css';
import LoadingSpinner from '../components/LoadingSpinner';
import ImageUploader from '../components/ImageUploader';

const parseFacebookEmbed = (input) => {
    if (!input) return '';
    const trimmed = input.trim();
    if (trimmed.startsWith('<iframe') || trimmed.startsWith('<div')) {
        return trimmed;
    }
    if (trimmed.startsWith('http') || trimmed.includes('facebook.com')) {
        const url = encodeURIComponent(trimmed);
        return `<iframe src="https://www.facebook.com/plugins/post.php?href=${url}&show_text=true&width=500" width="100%" height="800" style="border:none;overflow:hidden" scrolling="auto" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>`;
    }
    return trimmed;
};

/* ───────────────────────────────────────────────
   Componente per testi editabili inline (admin)
─────────────────────────────────────────────── */
const InlineEditable = ({ tag: Tag, value, onChange, className, style, placeholder }) => {
    const ref = useRef(null);
    const [editing, setEditing] = useState(false);

    const handleBlur = () => {
        setEditing(false);
        if (ref.current) onChange(ref.current.innerText);
    };

    const handleKeyDown = (e) => {
        // Ctrl+Enter o Escape per uscire dalla modifica
        if (e.key === 'Escape' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
            ref.current.blur();
        }
    };

    useEffect(() => {
        if (editing && ref.current) {
            ref.current.focus();
            // Porta il cursore alla fine
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(ref.current);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }, [editing]);

    return (
        <Tag
            ref={ref}
            className={`${className} inline-editable`}
            style={style}
            contentEditable={editing}
            suppressContentEditableWarning
            onClick={() => setEditing(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            data-placeholder={placeholder}
            title="Clicca per modificare"
        >
            {value}
        </Tag>
    );
};

/* ───────────────────────────────────────────────
   Componente per Slider Trascinabile
─────────────────────────────────────────────── */
const DraggableSliderBlock = ({ block, isAdmin, updateSlide }) => {
    const sliderRef = useRef(null);
    const [isDown, setIsDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleMouseDown = (e) => {
        setIsDown(true);
        setStartX(e.pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDown(false);
    };

    const handleMouseUp = () => {
        setIsDown(false);
    };

    const handleMouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 2; // velocità di scorrimento
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    return (
        <div className="dynamic-slider-wrapper">
            <div 
                className={`dynamic-slider ${isDown ? 'active' : ''}`}
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                {(block.slides || []).map((slide, sIndex) => (
                    <div key={sIndex} className="slider-item">
                        {slide.url && (
                            <img
                                src={slide.url}
                                alt={slide.caption || `Slide ${sIndex + 1}`}
                                draggable="false"
                            />
                        )}
                        {slide.caption && <div className="slider-caption">{slide.caption}</div>}
                        {isAdmin && (
                            <div className="inline-slide-edit">
                                <input
                                    type="text"
                                    value={slide.url || ''}
                                    onChange={e => updateSlide(block.id, sIndex, 'url', e.target.value)}
                                    placeholder="URL immagine"
                                />
                                <input
                                    type="text"
                                    value={slide.caption || ''}
                                    onChange={e => updateSlide(block.id, sIndex, 'caption', e.target.value)}
                                    placeholder="Didascalia"
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <small className="slider-hint">← Scorri per vedere altre immagini →</small>
        </div>
    );
};

/* ───────────────────────────────────────────────
   DynamicPage principale
─────────────────────────────────────────────── */
const DynamicPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [notFound, setNotFound] = useState(false);

    const docId = location.pathname.startsWith('/') ? location.pathname.substring(1) : location.pathname;

    useEffect(() => {
        const fetchPageData = async () => {
            setLoading(true);
            try {
                const docRef = doc(db, 'dynamic_pages', docId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setPageData(docSnap.data());
                    if (docSnap.data().title) {
                        document.title = `${docSnap.data().title} | L'Antico Piol De Feltre`;
                    }
                } else {
                    setNotFound(true);
                }
            } catch (err) {
                console.error("Errore nel recupero della pagina dinamica:", err);
                setError('Si è verificato un errore nel caricamento della pagina.');
            } finally {
                setLoading(false);
            }
        };
        fetchPageData();
    }, [docId]);

    /* ── Aggiorna un campo di un blocco ── */
    const updateBlock = useCallback((blockId, field, value) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => b.id === blockId ? { ...b, [field]: value } : b)
        }));
    }, []);

    /* ── Aggiorna una slide di uno slider ── */
    const updateSlide = useCallback((blockId, slideIndex, field, value) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id !== blockId) return b;
                const slides = [...b.slides];
                slides[slideIndex] = { ...slides[slideIndex], [field]: value };
                return { ...b, slides };
            })
        }));
    }, []);

    /* ── Aggiorna un post della griglia ── */
    const updatePost = useCallback((blockId, postIndex, field, value) => {
        setPageData(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => {
                if (b.id !== blockId) return b;
                const posts = [...(b.posts || [])];
                posts[postIndex] = { ...posts[postIndex], [field]: value };
                return { ...b, posts };
            })
        }));
    }, []);

    /* ── Salva su Firebase ── */
    const handleSave = async () => {
        if (!currentUser || !pageData) return;
        setSaving(true);
        try {
            const pageRef = doc(db, 'dynamic_pages', docId);
            await setDoc(pageRef, {
                ...pageData,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.email
            }, { merge: true });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error("Errore salvataggio:", err);
            alert("Errore durante il salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    /* ── Allineamento ── */
    const getAlignmentClass = (align) => {
        if (align === 'center') return 'align-center';
        if (align === 'right') return 'align-right';
        return 'align-left';
    };

    if (loading) return <LoadingSpinner show={true} />;
    if (notFound) return <NotFoundPage />;

    if (error) {
        return (
            <div className="dynamic-page error-page">
                <section className="page-header">
                    <div className="container"><h1>Ops!</h1></div>
                </section>
                <div className="container" style={{ textAlign: 'center', padding: '40px 0' }}>
                    <p>{error}</p>
                    <button className="cta-button" onClick={() => navigate('/')}>Torna alla Home</button>
                </div>
            </div>
        );
    }

    if (!pageData) return null;

    const isAdmin = !!currentUser;

    return (
        <div className="dynamic-page">

            {/* ── Barra admin inline ────────────────── */}
            {isAdmin && (
                <div className="inline-admin-bar">
                    <span className="inline-admin-label">✏️ Modalità Modifica Attiva</span>
                    <div className="inline-admin-actions">
                        <button
                            className="inline-save-btn"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? '⏳ Salvataggio...' : saved ? '✅ Salvato!' : '💾 Salva Modifiche'}
                        </button>
                        <button
                            className="inline-builder-btn"
                            onClick={() => navigate(`/admin-page-builder${location.pathname}`)}
                        >
                            🎨 Apri Builder
                        </button>
                    </div>
                </div>
            )}

            <div className="dynamic-content-container container">
                {(pageData.blocks || []).map((block) => {
                    const alignClass = getAlignmentClass(block.align);

                    switch (block.type) {

                        case 'h1':
                            return isAdmin
                                ? <InlineEditable key={block.id} tag="h1" className={`dynamic-h1 ${alignClass}`} value={block.content} onChange={v => updateBlock(block.id, 'content', v)} placeholder="Titolo H1" />
                                : <h1 key={block.id} className={`dynamic-h1 ${alignClass}`}>{block.content}</h1>;

                        case 'h2':
                            return isAdmin
                                ? <InlineEditable key={block.id} tag="h2" className={`dynamic-h2 ${alignClass}`} value={block.content} onChange={v => updateBlock(block.id, 'content', v)} placeholder="Sottotitolo H2" />
                                : <h2 key={block.id} className={`dynamic-h2 ${alignClass}`}>{block.content}</h2>;

                        case 'p':
                            return isAdmin
                                ? <InlineEditable key={block.id} tag="p" className={`dynamic-p ${alignClass}`} style={{ whiteSpace: 'pre-line' }} value={block.content} onChange={v => updateBlock(block.id, 'content', v)} placeholder="Testo paragrafo..." />
                                : <p key={block.id} className={`dynamic-p ${alignClass}`} style={{ whiteSpace: 'pre-line' }}>{block.content}</p>;

                        case 'image':
                            return (
                                <div key={block.id} className={`dynamic-image-wrapper ${alignClass}`}>
                                    {block.url && <img src={block.url} alt="Contenuto pagina" className="dynamic-image" draggable="false" />}
                                    {isAdmin && (
                                        <div className="inline-image-edit">
                                            <span>🖼️ URL Immagine:</span>
                                            <input
                                                type="text"
                                                value={block.url || ''}
                                                onChange={e => updateBlock(block.id, 'url', e.target.value)}
                                                placeholder="https://... oppure /images/..."
                                            />
                                        </div>
                                    )}
                                </div>
                            );

                        case 'button':
                            return (
                                <div key={block.id} className={`dynamic-button-wrapper ${alignClass}`}>
                                    <a 
                                        href={block.buttonLink} 
                                        className="cta-button"
                                        target={block.buttonLink?.startsWith('http') ? '_blank' : '_self'}
                                        rel={block.buttonLink?.startsWith('http') ? 'noopener noreferrer' : ''}
                                    >
                                        {block.buttonLabel || 'Scopri di più'}
                                    </a>
                                    {isAdmin && (
                                        <div className="inline-button-edit">
                                            <input
                                                type="text"
                                                value={block.buttonLabel || ''}
                                                onChange={e => updateBlock(block.id, 'buttonLabel', e.target.value)}
                                                placeholder="Testo pulsante"
                                            />
                                            <input
                                                type="text"
                                                value={block.buttonLink || ''}
                                                onChange={e => updateBlock(block.id, 'buttonLink', e.target.value)}
                                                placeholder="Link (es. /contatti)"
                                            />
                                        </div>
                                    )}
                                </div>
                            );

                        case 'slider':
                            return <DraggableSliderBlock key={block.id} block={block} isAdmin={isAdmin} updateSlide={updateSlide} />;

                        case 'post-grid':
                            return (
                                <div key={block.id} className="dynamic-post-grid">
                                    {(block.posts || []).map((post, pIndex) => (
                                        <div key={pIndex} className="post-grid-item">
                                            {post.type === 'facebook' ? (
                                                <div className="facebook-post-wrapper">
                                                    {(post.embedCode || post.url) ? (
                                                        <div 
                                                            className="facebook-embed-container"
                                                            dangerouslySetInnerHTML={{ __html: parseFacebookEmbed(post.embedCode || post.url) }} 
                                                            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                                                        />
                                                    ) : (
                                                        <div className="placeholder-box">Inserisci il codice Incorpora o il link del post Facebook</div>
                                                    )}
                                                    {isAdmin && (
                                                        <div className="inline-post-edit">
                                                            <textarea
                                                                value={post.embedCode !== undefined ? post.embedCode : (post.url || '')}
                                                                onChange={e => updatePost(block.id, pIndex, 'embedCode', e.target.value)}
                                                                placeholder='Codice Incorpora o Link Facebook'
                                                                rows="3"
                                                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #c7d2fe' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="internal-post-card">
                                                    {post.image && <img src={post.image} alt={post.title} className="internal-post-image" />}
                                                    <div className="internal-post-content">
                                                        {isAdmin ? (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    value={post.title || ''}
                                                                    onChange={e => updatePost(block.id, pIndex, 'title', e.target.value)}
                                                                    placeholder="Titolo"
                                                                    className="internal-post-title-input"
                                                                />
                                                                <textarea
                                                                    value={post.text || ''}
                                                                    onChange={e => updatePost(block.id, pIndex, 'text', e.target.value)}
                                                                    placeholder="Testo del post"
                                                                    className="internal-post-text-input"
                                                                    rows="4"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={post.buttonLabel || ''}
                                                                    onChange={e => updatePost(block.id, pIndex, 'buttonLabel', e.target.value)}
                                                                    placeholder="Testo Pulsante"
                                                                    style={{marginBottom: '5px'}}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={post.buttonLink || ''}
                                                                    onChange={e => updatePost(block.id, pIndex, 'buttonLink', e.target.value)}
                                                                    placeholder="Link Pulsante"
                                                                    style={{marginBottom: '5px'}}
                                                                />
                                                                <div style={{marginTop: '5px'}}>
                                                                    <ImageUploader 
                                                                        currentImageUrl={post.image || ''}
                                                                        onUploadComplete={(url) => updatePost(block.id, pIndex, 'image', url)}
                                                                        folderPath="post_images"
                                                                    />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {post.title && <h3 className="internal-post-title">{post.title}</h3>}
                                                                {post.text && <p className="internal-post-text" style={{whiteSpace: 'pre-line'}}>{post.text}</p>}
                                                            </>
                                                        )}
                                                        {post.buttonLabel && post.buttonLink && !isAdmin && (
                                                            <a 
                                                                href={post.buttonLink} 
                                                                className="cta-button internal-post-btn"
                                                                target={post.buttonLink.startsWith('http') ? '_blank' : '_self'}
                                                                rel={post.buttonLink.startsWith('http') ? 'noopener noreferrer' : ''}
                                                            >
                                                                {post.buttonLabel}
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );

                        case 'js':
                            return (
                                <div key={block.id} className="dynamic-js-wrapper">
                                    {isAdmin && (
                                        <div className="inline-js-edit">
                                            <span>⚙️ Codice JS/HTML (modifica dal Builder)</span>
                                            <button onClick={() => navigate(`/admin-page-builder${location.pathname}`)}>Apri Builder</button>
                                        </div>
                                    )}
                                    <div dangerouslySetInnerHTML={{ __html: block.content }} />
                                </div>
                            );

                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};

export default DynamicPage;
